import { Alert, Form, Modal, Select, Spin } from 'antd';
import { reassignIncidenceRequestSupervisor } from 'api/incidence-request.api';
import { getAllUsers } from 'api/user.api';
import { useEffect, useState } from 'react';
import { ApiError, User } from 'types';
import { IncidenceRequestDetailRecord } from 'types/incidenceRequests';
import { IncidenceRequestPerson } from './incidenceRequest.shared';

type SupervisorOption = {
  label: string;
  value: string;
};

type IncidenceRequestReassignSupervisorModalProps = {
  open: boolean;
  requestId?: string;
  requester?: IncidenceRequestPerson;
  onCancel: () => void;
  onSuccess: (payload: {
    record: IncidenceRequestDetailRecord;
    supervisorLabel: string;
  }) => void;
};

const getErrorMessage = (error: unknown, fallbackMessage: string) => {
  if (error instanceof ApiError) {
    return error.message;
  }

  if (error instanceof Error && error.message) {
    return error.message;
  }

  return fallbackMessage;
};

const isFormValidationError = (error: unknown) =>
  Boolean(
    error &&
    typeof error === 'object' &&
    'errorFields' in error &&
    Array.isArray(error.errorFields)
  );

const buildSupervisorLabel = (
  user: Pick<User, '_id' | 'idEmployee' | 'name' | 'lastName'>
) => ({
  label: `${user.name} ${user.lastName} (${user.idEmployee})`,
  value: user._id,
});

export const IncidenceRequestReassignSupervisorModal = ({
  open,
  requestId,
  requester,
  onCancel,
  onSuccess,
}: IncidenceRequestReassignSupervisorModalProps) => {
  const [form] = Form.useForm<{ supervisorId: string }>();
  const [supervisorOptions, setSupervisorOptions] = useState<
    SupervisorOption[]
  >([]);
  const [loadingSupervisors, setLoadingSupervisors] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) {
      form.resetFields();
      setError(null);
      setSupervisorOptions([]);
      setLoadingSupervisors(false);
      setSubmitting(false);
      return;
    }

    let cancelled = false;

    const loadSupervisors = async () => {
      try {
        setLoadingSupervisors(true);
        setError(null);
        const users = await getAllUsers();
        if (cancelled) {
          return;
        }

        const nextOptions = users
          .filter((user) => user.status && user._id !== requester?.id)
          .sort((firstUser, secondUser) =>
            `${firstUser.name} ${firstUser.lastName}`.localeCompare(
              `${secondUser.name} ${secondUser.lastName}`
            )
          )
          .map(buildSupervisorLabel);

        setSupervisorOptions(nextOptions);
      } catch (loadError) {
        if (cancelled) {
          return;
        }

        setSupervisorOptions([]);
        setError(
          getErrorMessage(
            loadError,
            'No fue posible cargar los supervisores disponibles.'
          )
        );
      } finally {
        if (!cancelled) {
          setLoadingSupervisors(false);
        }
      }
    };

    void loadSupervisors();

    return () => {
      cancelled = true;
    };
  }, [form, open, requester?.id]);

  const handleSubmit = async () => {
    if (!requestId) {
      return;
    }

    try {
      setError(null);
      const { supervisorId } = await form.validateFields();
      setSubmitting(true);
      const response = await reassignIncidenceRequestSupervisor(requestId, {
        supervisorId,
      });
      const supervisorLabel =
        supervisorOptions.find((option) => option.value === supervisorId)
          ?.label ?? 'Supervisor seleccionado';

      onSuccess({
        record: response.data,
        supervisorLabel,
      });
      form.resetFields();
    } catch (submitError) {
      if (isFormValidationError(submitError)) {
        return;
      }

      setError(
        getErrorMessage(
          submitError,
          'No fue posible asignar el nuevo supervisor.'
        )
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      open={open}
      title="Asignar supervisor"
      okText="Guardar"
      cancelText="Cancelar"
      destroyOnClose
      okButtonProps={{
        loading: submitting,
        disabled: loadingSupervisors,
      }}
      cancelButtonProps={{
        disabled: submitting,
      }}
      onCancel={onCancel}
      onOk={() => void handleSubmit()}
      data-cy="incidence-request-reassign-supervisor-modal"
    >
      <p data-cy="incidence-request-reassign-supervisor-copy">
        El supervisor que selecciones será el nuevo supervisor del empleado de
        ahora en adelante.
      </p>
      {requester ? (
        <p>
          Empleado: <strong>{requester.fullName}</strong>
        </p>
      ) : null}

      {error ? (
        <Alert
          className="mb-3"
          type="error"
          showIcon
          message={error}
          data-cy="incidence-request-reassign-supervisor-error"
        />
      ) : null}

      <Form form={form} layout="vertical">
        <Form.Item
          name="supervisorId"
          label="Nuevo supervisor"
          rules={[
            {
              required: true,
              message: 'Selecciona un supervisor para continuar.',
            },
          ]}
        >
          <Select
            showSearch
            placeholder="Selecciona un supervisor"
            loading={loadingSupervisors}
            disabled={loadingSupervisors || submitting}
            options={supervisorOptions}
            optionFilterProp="label"
            filterOption={(input, option) =>
              String(option?.label ?? '')
                .toLowerCase()
                .includes(input.toLowerCase())
            }
            notFoundContent={
              loadingSupervisors ? (
                <Spin size="small" />
              ) : (
                'No hay supervisores disponibles.'
              )
            }
            data-cy="incidence-request-reassign-supervisor-select"
          />
        </Form.Item>
      </Form>
    </Modal>
  );
};
